import { COLD_WEATHER_BATTERY_VERSION } from '~/devices/device-helpers';
import '../actions/assign-installer-button';
import '../actions/work-order-bin-dimensions';
import '../actions/work-order-bin-location';
import '../actions/work-order-bin-photo';
import '../actions/work-order-bin-serial';
import '../actions/work-order-device';
import '../actions/work-order-device-battery';
import './work-order-actionbar';
import './work-order-bin';
import './work-order-bin-confirm';
import './work-order-bin-heading';
import './work-order-details';
import './work-order-diagnosis';
import './work-order-location-details';
import { entityStore } from '~/utility/BinSentry-ui-utility';
import { LitEntity, html, css, renderIf } from '../../utility/lit-entity';
import { sharedStyles } from '../../styles/shared-styles';

class WorkOrderView extends LitEntity {
  constructor() {
    super();
    this.currentStep = 0;
  }

  static get properties() {
    return {
      location: { type: Object, attribute: false },
      confirmed: { type: Boolean, attribute: false },
      steps: { type: Array, attribute: false },
      currentStep: { type: Number, attribute: false },
      binEntity: { type: Object, attribute: false },
      deviceEntity: { type: Object, attribute: false },
      googleMapsKey: { type: String, attribute: 'google-maps-key' },
    };
  }

  static get styles() {
    return [sharedStyles, css`
      :host {
        display: block;
      }
      .heading {
        margin-left: var(--root-margin, 1rem);
      }
      .actions {
        margin-left: var(--root-margin, 1rem);
      }
      .content {
        max-height: 2000px;
        overflow: hidden;
        transition: max-height 0.6s cubic-bezier(0.7, 0, 0.84, 0);
      }
      bn-surface[collapsed] .content {
        max-height: 0;
        transition: max-height 0.6s cubic-bezier(0.16, 1, 0.3, 1);
      }
      bn-surface[collapsed]:hover {
        cursor: pointer;
        background-color: var(--hover-color);
      }
      .actions {
        display: flex;
        margin-top: 1em;
      }
      .action-button {
        margin-right: 8px;
      }
      .next {
        margin-top: 1rem;
        display: flex;
        justify-content: flex-end;
      }
    `];
  }

  renderEntity() {
    return html`
      <lit-entity .href="${this.binHref}" @update="${this._onUpdateBin}"></lit-entity>
      <lit-entity .href="${this.deviceHref}" @update="${this._onUpdateDevice}"></lit-entity>
      <div class="layout">
        <bn-surface>
          <work-order-details .href="${this.href}"></work-order-details>
          ${this._renderActions()}
        </bn-surface>

        <div class="heading">
          <work-order-bin-heading .href="${this.binHref}"></work-order-bin-heading>
          <work-order-location-details .href="${this.binHref}"></work-order-location-details>
        </div>

        ${this.steps.map(step => this._renderStep(step))}
      </div>

      <work-order-actionbar .href="${this.href}"></work-order-actionbar>
    `;
  }

  _renderStep(step) {
    const index = this.steps.indexOf(step);
    return html`
      <bn-surface ?collapsed="${this.currentStep !== index}" @click="${() => this.currentStep = index}">
        <h3>${step.title}</h3>
        <div class="content">
          ${step.content}
          ${renderIf(index !== this.steps.length - 1, () => html`
            <div class="next">
              <button class="button button--tertiary" @click="${this._next}">
                Next Step
                <bn-icon name="far fa-angle-right"></bn-icon>
              </button>
            </div>
          `)}
        </div>
      </bn-surface>
    `;
  }

  _next(e) {
    e.stopPropagation();
    ++this.currentStep;
  }

  _renderActions() {
    return html`
      <div class="actions">
        ${renderIf(this.entity.hasActionByName('select-installer'), () => html`
          <assign-installer-button class="action-button" .href="${this.href}"></assign-installer-button>
        `)}

        ${renderIf(this.entity.hasActionByName('reopen-work-order'), () => html`
          <bn-action
            class="action-button"
            .href="${this.href}"
            actionname="reopen-work-order"
            submittext="Reopen"
            @response="${this._onSuccess}"
            suppressheader
          ></bn-action>
        `)}

        ${renderIf(this.entity.hasActionByName('unassign-work-order'), () => html`
          <bn-action
            class="action-button"
            .href="${this.href}"
            actionname="unassign-work-order"
            submittext="Unassign"
            @response="${this._onSuccess}"
            suppressheader
          ></bn-action>
        `)}

        ${renderIf(this.entity.hasActionByName('close-work-order'), () => html`
          <bn-action
            class="action-button"
            .href="${this.href}"
            actionname="close-work-order"
            submittext="Close"
            @response="${this._onSuccess}"
            suppressheader
          ></bn-action>
        `)}

        ${renderIf(this.entity.hasActionByName('request-work-order'), () => html`
          <bn-action
            class="action-button"
            .href="${this.href}"
            actionname="request-work-order"
            submittext="Request"
            @response="${this._onSuccess}"
            suppressheader
          ></bn-action>
        `)}
      </div>
    `;
  }

  _onUpdateBin(e) {
    const { entity } = e.detail;
    this.binEntity = entity;
    this._buildSteps();
  }

  _onUpdateDevice(e) {
    const { entity } = e.detail;
    this.deviceEntity = entity;
    this._buildSteps();
  }

  _entityChanged(entity) {
    super._entityChanged(entity);
    this.binHref = this.getHref('https://api.binsentry.com/rel/bin');
    this.deviceHref = this.entity?.getSubEntityByClasses(['device', 'active'])?.href;
    this._buildSteps();
  }

  _buildSteps() {
    if (!this.entity) {
      this.steps = [];
      return;
    }
    const steps = [];

    const updateLocationAction = this.entity.getAction('update-location');
    if (updateLocationAction) {
      steps.push({
        title: 'Bin Location',
        content: html`
          <work-order-bin-location .href="${this.href}" show-map .googleMapsKey="${this.googleMapsKey}"></work-order-bin-location>
        `,
        required: true,
        completed: updateLocationAction.getField('location[latitude]').value
          && updateLocationAction.getField('location[latitude]').value,
      });
    }

    if (this.entity.hasActionByName('add-serial-number')) {
      steps.push({
        title: 'BinSentry QR Code Sticker',
        content: html`
          <work-order-bin-serial .href="${this.href}"></work-order-bin-serial>
        `,
        required: true,
        completed: this.binEntity?.properties.serialNumber,
      });
    }

    if (this.entity.hasActionByName('get-image-upload')) {
      steps.push({
        title: 'Bin Photo',
        content: html`
          <work-order-bin-photo .href="${this.href}"></work-order-bin-photo>
        `,
        required: true,
        completed: this.binEntity?.hasSubEntityByRel('https://api.binsentry.com/rel/bin-photo'),
      });
    }

    const hasAttachedDevice = this.entity.getSubEntityByClasses(['device', 'active']);
    steps.push({
      title: 'Device',
      content: html`
        <work-order-device .href="${this.href}"></work-order-device>
      `,
      required: true,
      completed: this.entity.properties.requestType === 'removal' ? !hasAttachedDevice : hasAttachedDevice,
    });
    const isBatteryUpgradeDiagnosis = this.entity.properties.preDiagnosis === 'Battery Upgrade';
    const batteryUpdatedOnWorkOrder = this.entity.properties.history?.find(item => item.action === 'device:update-battery');
    const hasColdWeatherBattery = this.deviceEntity?.properties.batteryVersion === COLD_WEATHER_BATTERY_VERSION;
    if (isBatteryUpgradeDiagnosis || batteryUpdatedOnWorkOrder || !hasColdWeatherBattery) {
      steps.push({
        title: 'Battery',
        content: html`
          <work-order-device-battery .href="${this.href}"></work-order-device-battery>
        `,
        required: isBatteryUpgradeDiagnosis,
        completed: isBatteryUpgradeDiagnosis
          ? this.deviceEntity?.properties.batteryVersion // They must submit the action to update battery version
          : true, // Battery Upgrade is optional, allow them to proceed without upgrading
      });
    }

    const updateDimensionsAction = this.entity.getAction('update-dimensions');
    if (updateDimensionsAction) {
      steps.push({
        title: 'Bin Dimensions',
        content: html`
          <work-order-bin-dimensions .href="${this.href}"></work-order-bin-dimensions>
        `,
        required: true,
        completed: updateDimensionsAction.getField('dimensions[deviceDepth]')?.value
          && updateDimensionsAction.getField('dimensions[body][radius]')?.value
          && updateDimensionsAction.getField('dimensions[body][height]')?.value
          && updateDimensionsAction.getField('dimensions[hopper][height]')?.value,
      });
    }

    const updateFieldDiagnosisAction = this.entity.getAction('update-field-diagnosis');
    const updateFieldActionAction = this.entity.getAction('update-field-action');
    const hasDiagnosisActions = this.entity.hasActionByName('update-pre-diagnosis') ||
      updateFieldDiagnosisAction ||
      updateFieldActionAction;

    if (hasDiagnosisActions) {
      steps.push({
        title: 'Diagnosis',
        content: html`
          <work-order-diagnosis .href="${this.href}"></work-order-diagnosis>
        `,
        required: true,
        completed: (!updateFieldDiagnosisAction || updateFieldDiagnosisAction.getField('fieldDiagnosis').value)
          && (!updateFieldActionAction || updateFieldActionAction.getField('fieldAction').value),
      });
    }

    if (this.entity.properties.workOrderLocationState === 'assigned') {
      steps.push({
        title: 'Complete Work Order',
        content: this.entity.hasActionByName('complete-work-order')
          ? html`
            ${hasAttachedDevice && html`
              <p class="subdued">
                Completing the work order will set the device into <strong>Run Mode</strong>.
              </p>
            `}
            <bn-action
              .href="${this.href}"
              actionname="complete-work-order"
              submittext="Complete Work Order"
              @response="${this._onComplete}"
              suppressheader
            ></bn-action>
          `
          : html`
            <p>
              Complete the following required steps to complete work order:
            </p>
            <ul>
              ${steps.filter(step => step.required && !step.completed).map(step => html`<li>${step.title}</li>`)}
            </ul>
          `,
      });
    }

    this.steps = steps;
  }

  _onSuccess() {
    entityStore.invalidateHref(this.href);
  }

  _onComplete() {
    this._onSuccess();
    this.dispatchEvent(new CustomEvent('complete'));
  }
}

window.customElements.define('work-order-view', WorkOrderView);
